import React, { useState } from 'react';

interface NodeGroup {
    label: string;
    icon: string;
    color: string;
    items: { type: string; label: string; icon: string; description: string }[];
}

const nodeGroups: NodeGroup[] = [
    {
        label: 'Browser',
        icon: '🌍',
        color: '#1B5E20',
        items: [
            { type: 'open_browser', label: 'Open Browser', icon: '🌍', description: 'Launch browser and navigate to URL' },
            { type: 'close_browser', label: 'Close Browser', icon: '❌', description: 'Close the browser' },
        ],
    },
    {
        label: 'UI Interactions',
        icon: '🖱️',
        color: '#1565C0',
        items: [
            { type: 'click', label: 'Click', icon: '👆', description: 'Click an element' },
            { type: 'hover', label: 'Hover', icon: '🖱️', description: 'Hover over an element' },
            { type: 'write', label: 'Write', icon: '✍️', description: 'Type text into an input' },
            { type: 'get_element', label: 'Get Element', icon: '🔍', description: 'Extract a single element' },
            { type: 'get_elements', label: 'Get Elements', icon: '📋', description: 'Extract multiple elements' },
            { type: 'switch_to_new_window', label: 'Switch Window', icon: '🪟', description: 'Switch to new browser tab/window' },
            { type: 'switch_to_frame', label: 'Switch Frame', icon: '🖼️', description: 'Switch to iframe by index' },
            { type: 'wait_for_url_change', label: 'Wait URL Change', icon: '🔗', description: 'Wait until the page URL changes' },
            { type: 'wait_for_element', label: 'Wait Element', icon: '⏳', description: 'Wait for element to appear or disappear' },
        ],
    },
    {
        label: 'Logic & Loops',
        icon: '🔁',
        color: '#6A1B9A',
        items: [
            { type: 'if', label: 'If Condition', icon: '❓', description: 'Conditional branch' },
            { type: 'while', label: 'While Loop', icon: '🔄', description: 'Repeat while condition is true' },
            { type: 'loop', label: 'For Each Loop', icon: '➰', description: 'Iterate over a collection' },
        ],
    },
    {
        label: 'Network',
        icon: '🌐',
        color: '#00695C',
        items: [
            { type: 'api_call', label: 'API Call', icon: '🌐', description: 'Make an HTTP request' },
            { type: 'webhook', label: 'Webhook', icon: '🪝', description: 'Listen for incoming HTTP request' },
        ],
    },
    {
        label: 'Utilities',
        icon: '⏱️',
        color: '#E65100',
        items: [
            { type: 'sleep', label: 'Sleep', icon: '⏱️', description: 'Wait for a duration' },
            { type: 'code', label: 'Code', icon: '💻', description: 'Run custom JavaScript code' },
        ],
    },
    {
        label: 'Storage',
        icon: '📁',
        color: '#795548',
        items: [
            { type: 'last_file', label: 'Last File', icon: '📄', description: 'Get the name of the last file in storage' },
            { type: 'rename_file', label: 'Rename File', icon: '✏️', description: 'Rename a file in storage' },
            { type: 'create_file', label: 'Create File', icon: '📝', description: 'Create a PDF or CSV file in storage' },
            { type: 'read_file', label: 'Read File', icon: '📖', description: 'Read a PDF or CSV file from storage' },
        ],
    },
    {
        label: 'Agents',
        icon: '🤖',
        color: '#6200EA',
        items: [
            { type: 'ai_agent', label: 'AI Agent', icon: '🤖', description: 'Send a prompt to an AI model and store the response' },
        ],
    },
];

const NodePanel: React.FC = () => {
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const toggleGroup = (label: string) => {
        setCollapsed(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const onDragStart = (event: React.DragEvent, actionType: string) => {
        event.dataTransfer.setData('application/reactflow', actionType);
        event.dataTransfer.effectAllowed = 'move';
    };

    return (
        <div className="node-panel">
            <h2 style={{ margin: '0 0 14px', fontSize: 16, color: '#333' }}>🧩 Actions</h2>
            {nodeGroups.map(group => (
                <div key={group.label} style={{ marginBottom: 10 }}>
                    {/* Group header */}
                    <div
                        onClick={() => toggleGroup(group.label)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '7px 10px',
                            background: group.color,
                            color: 'white',
                            borderRadius: collapsed[group.label] ? 6 : '6px 6px 0 0',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontWeight: 'bold',
                            fontSize: 13,
                        }}
                    >
                        <span>{group.icon}</span>
                        <span style={{ flex: 1 }}>{group.label}</span>
                        <span style={{ fontSize: 11, opacity: 0.85 }}>
                            {collapsed[group.label] ? '▶' : '▼'}
                        </span>
                    </div>

                    {/* Group items */}
                    {!collapsed[group.label] && (
                        <div style={{ border: `1px solid ${group.color}`, borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
                            {group.items.map((item, idx) => (
                                <div
                                    key={item.type}
                                    className="action-item"
                                    draggable
                                    onDragStart={(e) => onDragStart(e, item.type)}
                                    style={{
                                        borderRadius: idx === group.items.length - 1 ? '0 0 5px 5px' : 0,
                                        borderBottom: idx < group.items.length - 1 ? '1px solid #eee' : 'none',
                                        margin: 0,
                                        border: 'none',
                                    }}
                                >
                                    <span className="action-icon" style={{ fontSize: 18 }}>{item.icon}</span>
                                    <div className="action-info">
                                        <div className="action-label" style={{ fontSize: 13 }}>{item.label}</div>
                                        <div className="action-description">{item.description}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default NodePanel;